import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  NominatedAccountStatusEnum,
  defaultAispGocardlessFlowRedirectionConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { EMPTY, catchError, firstValueFrom, tap } from 'rxjs';
import { ClearFormDataActionWithId, SetFormActionWithId } from '../../../actions/form-submission.action';
import { GetNominatedAccountStatus } from '../../../actions/savings-account.action';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { FormActionState } from '../../../state/form-submision.state';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { AccountErrorCodes } from '@app/translations';
import { HttpErrorResponse } from '@angular/common/http';

export function processNominatedAccount(injector: Injector) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const savingsAccountService = injector.get(SavingsAccountService);
  const selectedAccountId = store.selectSnapshot(FormActionState.getFormActionStateWithId('AccountSelection'))
    ?.formData?.accountId;
  const requestId = sessionStorage.getItem('requestId');
  if (!selectedAccountId || !requestId) return;
  savingsAccountService
    .processNominatedAccount(requestId, selectedAccountId)
    .pipe(
      tap((res) => {
        if (res) {
          store.dispatch(new ClearFormDataActionWithId('AccountSelection'));
          store.dispatch(new ClearFormDataActionWithId('NominatedAccounts'));
          if (res.status === 'processing') {
            handleProcessingStateOnAccount(injector, requestId, 0, selectedAccountId);
          }
          redirectUserToStatusPage(res.status, store, router, res?.accountIds);
        } else {
          console.error('No response received from processNominatedAccount');
          // take redirection config from project settings state or fallback to default
          const redirectionConfig =
            store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
              ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

          router.navigateByUrl(redirectionConfig[NominatedAccountStatusEnum.ERROR], { replaceUrl: true });
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        const { alertCodes = '' } = err.error;
        // take redirection config from project settings state or fallback to default
        const redirectionConfig =
          store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
            ?.aispFlowRedirectionConfig ?? defaultAispGocardlessFlowRedirectionConfig;

        if (alertCodes && alertCodes === AccountErrorCodes.NOMINATED_ACCOUNT_MISMATCH) {
          const nominatedAccountMismatchRedirect = redirectionConfig['nominated-account-mismatch'];
          if (nominatedAccountMismatchRedirect?.length)
            router.navigateByUrl(nominatedAccountMismatchRedirect, { replaceUrl: true });
          return EMPTY;
        }

        router.navigateByUrl(redirectionConfig[NominatedAccountStatusEnum.ERROR], { replaceUrl: true });
        return EMPTY;
      }),
    )
    .subscribe();
}

/**
 * A common method being used to redirect the user to specified url based on the status
 * Update carefully to avoid any breakage, this is also being used in nominated-account-status component
 * which the user first lands on after external AISP flow
 * Add other cases if need to do something specific for other statuses
 * @param status
 * @param store
 * @param accountIds
 */
export async function redirectUserToStatusPage(
  status: NominatedAccountStatusEnum,
  store: Store,
  router: Router,
  accountIds?: string[],
) {
  // take redirection config from project settings state or fallback to default
  const redirectionConfig =
    store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig?.aispFlowRedirectionConfig ||
    defaultAispGocardlessFlowRedirectionConfig;

  let urlToNavigate = '';
  switch (status) {
    case NominatedAccountStatusEnum.CONNECTED:
    case NominatedAccountStatusEnum.PROCESSING: {
      sessionStorage.removeItem('requestId');
      await firstValueFrom(store.dispatch(new GetNominatedAccountStatus()));
      urlToNavigate = redirectionConfig[status];
      router.navigateByUrl(urlToNavigate);
      break;
    }
    case NominatedAccountStatusEnum.MANY_ACCOUNTS: {
      store.dispatch(
        new SetFormActionWithId(
          { type: 'NominatedAccounts', formData: { accountIds: accountIds } },
          'NominatedAccounts',
        ),
      );
      urlToNavigate = redirectionConfig[status];
      router.navigateByUrl(urlToNavigate);
      break;
    }
    // handle redirection of all other statuses
    default: {
      sessionStorage.removeItem('requestId');
      urlToNavigate = redirectionConfig[status];
      router.navigateByUrl(urlToNavigate);
      break;
    }
  }
}

export function handleProcessingStateOnAccount(
  injector: Injector,
  requestId: string,
  retryCount = 0,
  selectedAccountId?: string,
) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const savingsAccountService = injector.get(SavingsAccountService);

  if (retryCount >= 36) {
    // after 3 minutes of retries, redirect to error page
    const redirectionConfig =
      store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
        ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

    window.location.href = redirectionConfig[NominatedAccountStatusEnum.ERROR];
    return;
  }

  setTimeout(() => {
    savingsAccountService
      .processNominatedAccount(requestId, selectedAccountId)
      .pipe(
        tap((res) => {
          if (res) {
            if (res.status === 'processing') {
              handleProcessingStateOnAccount(injector, requestId, retryCount + 1, selectedAccountId);
            } else {
              redirectUserToStatusPage(res.status, store, router, res?.accountIds);
            }
          } else {
            console.error('No response received from processNominatedAccount');
            // take redirection config from project settings state or fallback to default
            const redirectionConfig =
              store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
                ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

            window.location.href = redirectionConfig[NominatedAccountStatusEnum.ERROR];
          }
        }),
        catchError((err) => {
          console.error(err);

          // take redirection config from project settings state or fallback to default
          const redirectionConfig =
            store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
              ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

          window.location.href = redirectionConfig[NominatedAccountStatusEnum.ERROR];
          return EMPTY;
        }),
      )
      .subscribe();
  }, 5000);
}
